/**
 * 레벨 시스템 API 서비스
 */

const AUTH_API_URL = process.env.REACT_APP_AUTH_API_URL || 'https://auth.ilouli.com';

/**
 * 토큰 조회
 */
const getToken = () => {
  const TOKEN_KEY = 'ilouli_token';
  const cookies = document.cookie.split(';');
  for (let cookie of cookies) {
    const [name, value] = cookie.trim().split('=');
    if (name === TOKEN_KEY) {
      return decodeURIComponent(value);
    }
  }
  try {
    return localStorage.getItem(`${TOKEN_KEY}_backup`);
  } catch (e) {
    return null;
  }
};

/**
 * Auth API 요청 헬퍼
 */
const authApiRequest = async (endpoint, options = {}) => {
  const token = getToken();
  const headers = {
    'Content-Type': 'application/json',
    ...(token && { Authorization: `Bearer ${token}` }),
    ...options.headers
  };

  const response = await fetch(`${AUTH_API_URL}${endpoint}`, {
    ...options,
    headers,
    credentials: 'include'
  });

  const data = await response.json();

  if (!response.ok) {
    const error = new Error(data.error || 'Request failed');
    error.status = response.status;
    throw error;
  }

  return data;
};

/**
 * 내 레벨 정보 조회
 */
export const getLevelInfo = async () => {
  return authApiRequest('/api/level/info');
};

/**
 * 출석 체크
 */
export const checkin = async () => {
  return authApiRequest('/api/level/checkin', { method: 'POST' });
};

/**
 * 경험치 내역 조회
 */
export const getExpHistory = async (limit = 20, offset = 0) => {
  return authApiRequest(`/api/level/history?limit=${limit}&offset=${offset}`);
};

/**
 * 레벨 칭호 목록
 */
export const getLevelTitles = async () => {
  return authApiRequest('/api/level/titles');
};

/**
 * 레벨별 경험치 테이블
 */
export const getExpTable = async () => {
  return authApiRequest('/api/level/exp-table');
};

/**
 * 내 랭킹 조회
 */
export const getMyRanking = async () => {
  return authApiRequest('/api/rankings/my');
};

/**
 * 랭킹 조회
 */
export const getRankings = async (type = 'level', limit = 20, offset = 0) => {
  return authApiRequest(`/api/rankings/${type}?limit=${limit}&offset=${offset}`);
};

/**
 * TOP 랭킹 조회
 */
export const getTopRankings = async () => {
  return authApiRequest('/api/rankings/top');
};

/**
 * 일일 퀘스트 조회
 */
export const getDailyQuests = async () => {
  return authApiRequest('/api/quests/daily');
};

/**
 * 퀘스트 보상 수령
 */
export const claimQuest = async (questType) => {
  return authApiRequest(`/api/quests/${questType}/claim`, { method: 'POST' });
};

/**
 * 모든 퀘스트 보상 수령
 */
export const claimAllQuests = async () => {
  return authApiRequest('/api/quests/claim-all', { method: 'POST' });
};

/**
 * 내 업적 조회
 */
export const getMyAchievements = async () => {
  return authApiRequest('/api/achievements/my');
};

/**
 * 업적 확인 및 보상 수령
 */
export const checkAchievements = async () => {
  return authApiRequest('/api/achievements/check', { method: 'POST' });
};

// ============================================================================
// 닉네임 API
// ============================================================================

/**
 * 닉네임 상태 조회
 */
export const getNicknameStatus = async () => {
  return authApiRequest('/api/nickname/status');
};

/**
 * 닉네임 유효성 검사
 */
export const validateNickname = async (nickname) => {
  return authApiRequest(`/api/nickname/validate?nickname=${encodeURIComponent(nickname)}`);
};

/**
 * 닉네임 중복 확인
 */
export const checkNickname = async (nickname) => {
  return authApiRequest(`/api/nickname/check/${encodeURIComponent(nickname)}`);
};

/**
 * 랜덤 닉네임 생성
 */
export const getRandomNickname = async () => {
  return authApiRequest('/api/nickname/random');
};

/**
 * 닉네임 설정/변경
 */
export const setNickname = async (nickname) => {
  return authApiRequest('/api/nickname', {
    method: 'POST',
    body: JSON.stringify({ nickname })
  });
};

export default {
  getLevelInfo,
  checkin,
  getExpHistory,
  getLevelTitles,
  getExpTable,
  getMyRanking,
  getRankings,
  getTopRankings,
  getDailyQuests,
  claimQuest,
  claimAllQuests,
  getMyAchievements,
  checkAchievements,
  getNicknameStatus,
  validateNickname,
  checkNickname,
  getRandomNickname,
  setNickname
};
